import React, { Component } from "react"
import Select from "react-select/async"
import searchIcon from "../assets/mag_glass.png"
import clearIcon from "../assets/clear.png"
import "./Dashboard.css"
import { filterByLastSix, getUser, getChallenges, adminDeregisterUser } from "../services"
import { Themes } from "../managers/ThemeManager"
import { Constants } from "../Constants"
import { withPermissions } from "../components/AuthenticationComponent"
import { DeviceInfoCard } from "../components/DeviceInfoCard"
import OmniAural from "omniaural"
import ContentModal from "../components/ContentModal"

const customStyles = {
  control: (previous) => ({
    ...previous,
    minWidth: "30vw",
    backgroundColor: "#000",
    border: "0 solid transparent",
    borderBottom: "1px solid #fff",
    boxShadow: "none",
    borderRadius: 0
  }),
  input: (previous) => ({
    ...previous,
    color: "#fff"
  }),
  singleValue: (previous) => ({
    ...previous,
    color: "#fff"
  }),
  option: (previous, { isSelected }) => {
    const colors = Themes.currentTheme().colors
    return {
      ...previous,
      backgroundColor: "transparent",
      color: isSelected ? colors.buttonText : null,
      ":hover": {
        backgroundColor: colors.accect1 + "dd"
      }
    }
  },
  valueContainer: (previous) => ({
    ...previous,
    color: "#fff"
  })
}

class Dashboard extends Component {
  constructor(props) {
    super(props)
    this.state = {
      userInfo: {},
      showLoader: false,
      searchResults: [],
      searchTerm: "",
      registeredDevices: [],
      deregisteredDevices: []
    }
  }

  fetchUserList = async (searchTerm) => {
    if (searchTerm.length > 6) {
      return this.state.searchResults.filter((res) => {
        return res.label.startsWith(searchTerm)
      })
    } else if (searchTerm.length > 4) {
      try {
        let response = await filterByLastSix(searchTerm)
        const searchResults = Object.keys(response.users)
          .map((userKey) => {
            return { value: response.users[userKey], label: userKey }
          })
          .sort((a, b) => (a.label > b.label ? 1 : a.label < b.label ? -1 : 0))

        this.setState({ searchResults, searchTerm })
        return searchResults
      } catch (err) {
        this.setState({ showLoader: false, searchResults: [], searchTerm: "" }, () => {
          window.alert("Network Error: ", err.message)
        })
      }
    } else {
      return []
    }
  }

  onChangeReason = (event) => {
    let reason = event.target.value
    this.setState({ deregReason: reason })
  }

  deregister = (deviceId, deregReason) => {
    const regKey = this.state.userInfo.registration_key
    this.setState({ showLoader: true }, async () => {
      await adminDeregisterUser({
        registration_key: regKey,
        device_id: deviceId,
        reason: deregReason
      })
        .then((response) => {
          if (response.code !== "1200") {
            throw response
          }
          OmniAural.hideModal()
          this.setUserInfo(regKey)
        })
        .catch((err) => {
          this.setState({ showLoader: false }, () => {
            window.alert("Network Error: ", err.message)
          })
        })
    })
  }

  setUserInfo = async (regKey) => {
    this.setState({ showLoader: true }, async () => {
      try {
        const userRes = await getUser(regKey)
        const userInfo = userRes.userInfo || {}
        const devices = userInfo.devices || []
        const challengeRes = await getChallenges(regKey)
        const challenges = challengeRes.challenges || []
        devices.sort((a, b) => {
          return new Date(b.updated_at) - new Date(a.updated_at)
        })

        const registeredDevices = []
        const deregisteredDevices = []
        for (const device of devices) {
          device.challenges = []
          for (const challenge of challenges) {
            if (challenge.device_id === device.device_id) {
              device.challenges.push(challenge)
              device.challenges.sort((a, b) => new Date(b.responded_at) - new Date(a.responded_at))
            }
          }

          if (device.deregistered_at) {
            deregisteredDevices.push(device)
          } else {
            registeredDevices.push(device)
          }
        }

        this.setState({
          userInfo,
          registeredDevices,
          deregisteredDevices,
          showLoader: false
        })
      } catch (err) {
        this.setState({ showLoader: false }, () => {
          window.alert("Network Error: ", err.message)
        })
      }
    })
  }

  onUserSelected = async (option) => {
    if (option && option.value) {
      await this.setUserInfo(option.value)
    }
  }

  render() {
    const images = Themes.currentTheme().images
    const { userInfo, registeredDevices, deregisteredDevices } = this.state

    return (
      <>
        <div>
          <img src={images.banner} alt="Army MobileConnect Dashboard" id="bannerImage" />
          <div id="searchContainer">
            <div id="searchLabel">FIND A USER</div>
            <Select
              loadingMessage={({ inputValue }) => {
                return inputValue.length < 5 ? "Type at least 5 characters to see matches" : null
              }}
              noOptionsMessage={({ inputValue }) => {
                return inputValue.length < 5
                  ? "Type at least 5 characters to see matches"
                  : "No matching users found"
              }}
              placeholder="username"
              value={this.state.searchTerm}
              onChange={this.onUserSelected}
              defaultOptions={[]}
              options
              loadOptions={this.fetchUserList}
              styles={customStyles}
              isClearable
              components={{
                DropdownIndicator: (options) => {
                  return options.hasValue ? null : (
                    <img src={searchIcon} alt="search icon" className="iconImages" />
                  )
                },
                IndicatorSeparator: () => {
                  return null
                },
                ClearIndicator: (options) => {
                  return (
                    <img
                      onClick={options.clearValue}
                      src={clearIcon}
                      alt="clear icon"
                      className="iconImages"
                    />
                  )
                }
              }}
            />
          </div>
          {!userInfo.registration_key ? null : (
            <div id="bodyContent">
              <h1>{userInfo.first_name + " " + userInfo.last_name}</h1>
              <h3 className="text-link">{userInfo.email_address}</h3>
              <hr />
              <h2>Registered Devices</h2>
              <div className="card-container">
                {registeredDevices?.map((device) => {
                  return (
                    <DeviceInfoCard
                      key={device.device_id}
                      device={device}
                      showDeviceId
                      userRegistrationKey={userInfo.registration_key}
                      isDefaultDevice={userInfo.default_device_id === device.device_id}
                      onDeregister={(deviceToDeregister) =>
                        OmniAural.showModal({
                          data: deviceToDeregister,
                          type: "admin_deregister",
                          onConfirm: this.deregister
                        })
                      }
                    />
                  )
                })}
              </div>
              <h2>Deregistered Devices</h2>
              <div className="card-container">
                {deregisteredDevices?.map((device) => {
                  return <DeviceInfoCard key={device.device_id} device={device} showDeviceId />
                })}
              </div>
            </div>
          )}
        </div>
        <div className={`loaderContainer ${this.state.showLoader ? "" : "hide"}`}>
          <div className="loader" />
        </div>
        <ContentModal />
      </>
    )
  }
}

export default withPermissions(Dashboard, [
  Constants.PERMISSION_TYPES.AUTHENTICATED,
  Constants.PERMISSION_TYPES.ADMINISTRATOR
])
